import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Gift, Factory, Cpu } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_COMPANY_TYPES = ['azienda_manifatturiera', 'azienda_automazione', 'end_user'];

const RegisterPage = () => {
  const { t } = useLanguage();
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // user_type from query param (?type=azienda_manifatturiera | azienda_automazione | end_user legacy)
  const initialType = searchParams.get('type');
  const userType = VALID_COMPANY_TYPES.includes(initialType) ? initialType : 'azienda_manifatturiera';
  const isAutomation = userType === 'azienda_automazione';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    vat_number: '',
    country: '',
    city: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [trialToken, setTrialToken] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [verifyingTrial, setVerifyingTrial] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const trial = searchParams.get('trial');
    if (trial) {
      verifyTrialToken(trial);
    }
    // Clear any legacy payment token leftover
    localStorage.removeItem('payment_token');
    localStorage.removeItem('registration_type');
  }, [searchParams]);

  const verifyTrialToken = async (token) => {
    setVerifyingTrial(true);
    try {
      const response = await axios.get(`${API}/trial/verify/${token}`);
      if (response.data.invite_type === 'professional') {
        navigate(`/register-professional?trial=${token}`);
        return;
      }
      setTrialToken(token);
      setTrialInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common_error'));
    } finally {
      setVerifyingTrial(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error(t('terms_required'));
      return;
    }
    setLoading(true);
    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        userType,
        null,           // no payment token (free model)
        trialToken,
        {
          company_name: formData.company_name || null,
          vat_number: formData.vat_number || null,
          country: formData.country || null,
          city: formData.city || null,
          phone: formData.phone || null,
        }
      );
      localStorage.removeItem('payment_token');
      localStorage.removeItem('registration_type');
      toast.success(trialToken ? t('register_trial_success') : t('register_success'));
      navigate('/directory');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Email already registered') {
        // Falso errore: il primo submit potrebbe essere riuscito. Proviamo login automatico.
        try {
          await login(formData.email, formData.password);
          toast.success(t('register_success'));
          navigate('/directory');
          return;
        } catch (_loginErr) {
          // Login fallito → email davvero di un altro account
          toast.error(t('auth_email_taken'));
        }
      } else {
        toast.error(detail || t('common_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifyingTrial) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">{t('register_verifying_invite')}</p>
        </div>
      </div>
    );
  }

  const headerIcon = isAutomation ? Cpu : Factory;
  const HeaderIcon = headerIcon;
  const headerKey = isAutomation ? 'register_automation_title' : 'register_manufacturer_title';
  const subKey = isAutomation ? 'register_automation_subtitle' : 'register_manufacturer_subtitle';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 py-12" data-testid="register-page">
      <Card className="w-full max-w-lg border border-slate-200 rounded-md shadow-sm">
        {trialToken && trialInfo && (
          <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-4 rounded-t-md">
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6" />
              <div>
                <p className="font-semibold">{t('trial_banner_title')}</p>
                <p className="text-sm opacity-90">{t('trial_banner_days').replace('{days}', trialInfo.duration_days)}</p>
              </div>
            </div>
          </div>
        )}

        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className={`w-14 h-14 rounded-md flex items-center justify-center ${isAutomation ? 'bg-blue-100' : 'bg-orange-100'}`}>
              <HeaderIcon className={`h-7 w-7 ${isAutomation ? 'text-blue-600' : 'text-orange-600'}`} />
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t(headerKey)}
          </CardTitle>
          <p className="text-sm text-slate-600 mt-2">{t(subKey)}</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
            {t('register_free_badge')}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company name */}
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium text-slate-700">{t('register_company_name')} *</Label>
              <Input
                id="company_name"
                name="company_name"
                type="text"
                required
                value={formData.company_name}
                onChange={handleChange}
                className="rounded-sm"
                data-testid="register-company-name"
              />
            </div>

            {/* Two columns: name + email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">{t('register_contact_person')} *</Label>
                <Input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className="rounded-sm" data-testid="register-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('auth_email')} *</Label>
                <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="rounded-sm" data-testid="register-email" />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t('auth_password')} *</Label>
              <Input id="password" name="password" type="password" required minLength={6} value={formData.password} onChange={handleChange} className="rounded-sm" data-testid="register-password" />
            </div>

            {/* Country + City */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="country" className="text-sm font-medium text-slate-700">{t('register_country')}</Label>
                <Input id="country" name="country" type="text" value={formData.country} onChange={handleChange} className="rounded-sm" data-testid="register-country" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="text-sm font-medium text-slate-700">{t('register_city')}</Label>
                <Input id="city" name="city" type="text" value={formData.city} onChange={handleChange} className="rounded-sm" data-testid="register-city" />
              </div>
            </div>

            {/* Phone + VAT */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">{t('register_phone')}</Label>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="rounded-sm" data-testid="register-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vat_number" className="text-sm font-medium text-slate-700">{t('register_vat')}</Label>
                <Input id="vat_number" name="vat_number" type="text" value={formData.vat_number} onChange={handleChange} className="rounded-sm" data-testid="register-vat" />
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm text-slate-600">
                {t('terms_accept')}{' '}
                <Link to="/terms" target="_blank" className="text-orange-500 hover:underline">{t('terms_link')}</Link>
                {' '}{t('terms_and')}{' '}
                <Link to="/privacy" target="_blank" className="text-orange-500 hover:underline">{t('privacy_link')}</Link>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm py-6 font-semibold"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('register_create_free_account')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            <span>{t('auth_have_account')} </span>
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="register-login-link">
              {t('nav_login')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
